// src/components/CreateSelectionGroup.tsx

import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Card } from 'react-bootstrap';
import { CreateSelectionGroup } from '../../../../../../../../crud/menu_api/selection_groups';
import { toast } from 'react-toastify';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import './CreateSelectionGroupModal.scss';


interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  local_id: number;
  separate_printing?: boolean;
  items_affected?: Array<{
    item_id: number;
    item_name: string;
  }>;
  items_contained?: Array<{
    item_id: number;
    item_name: string;
  }>;
}

interface CreateSelectionGroupProps {
  show: boolean;
  handleClose: () => void;
  data: SelectionGroupType[];
  setData: (items: SelectionGroupType[]) => void;
}

const CreateSelectionGroupModal: React.FC<CreateSelectionGroupProps> = ({ show, handleClose, data, setData }) => {
  const [groupName, setGroupName] = useState<string>('');
  const [groupDescription, setGroupDescription] = useState<string>('');
  const [separatePrinting, setSeparatePrinting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showInformation, setShowInformation] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validaciones básicas
    if (groupName.trim() === '') {
      toast.error('El nombre del grupo es obligatorio.');
      setLoading(false);
      return;
    }

    // Obtener local_id del usuario (asumiendo que está almacenado en localStorage)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;

    if (localId === 0) {
      toast.error('No se pudo obtener el ID local del usuario.');
      setLoading(false);
      return;
    }

    const payload = {
      selection_group_name: groupName,
      selection_group_description: groupDescription.trim() !== '' ? groupDescription : undefined,
      separate_printing: separatePrinting,
      local_id: localId,
    };

    CreateSelectionGroup(payload)
      .then((response) => {
        toast.success('Grupo de selección creado exitosamente.');
        // Asumiendo que response.data contiene el nuevo grupo creado
        // Agregar arrays vacíos para items_affected e items_contained si no existen
        const newGroup = {
          ...(response as any).data,
          items_affected: (response as any).data.items_affected || [],
          items_contained: (response as any).data.items_contained || []
        };
        setData([...data, newGroup]);
        // Reiniciar campos
        setGroupName('');
        setGroupDescription('');
        handleClose();
      })
      .catch((error) => {
        console.error('Error creando el grupo de selección', error);
        const message = error.response?.data?.message || 'Error al crear el grupo de selección.';
        toast.error(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleModalClose = () => {
    if (!loading) {
      setGroupName('');
      setGroupDescription('');
      setSeparatePrinting(false);
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" className="create-selection-group-modal">
      <Form onSubmit={handleSubmit}>
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>
              Crea y arma tu producto a elección
            </Modal.Title>
          </div>
          <Button 
            variant="none" 
            className="modal-close-button" 
            onClick={handleModalClose}
            disabled={loading}
          >
            <X size={24} />
          </Button>
        </Modal.Header>
        
        {loading && <MiniLoadingSpinner left='50%'/>}
        
        {!loading && (
          <Modal.Body>
            {/* Sección de Información */}
            <div className="information-section">
              <Button
                className="information-button"
                variant="none"
                onClick={() => setShowInformation(!showInformation)}
                type="button"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <FaInfoCircle /> 
                  <span>Información</span>
                </div>
                {!showInformation ? <FaChevronDown /> : <FaChevronUp />}
              </Button>
              
              {showInformation && (
                <Card>
                  <Card.Body>
                    <p>
                      En esta sección puedes configurar el armado de un producto base de tu carta con múltiples opciones de ingredientes que tengas en tu sección de ingredientes. Por ejemplo: Una Pizza (este sería tu producto base) tiene múltiples tipos como margarita, napolitana, etc.
                    </p>
                    <h5>Descripción de campos:</h5>
                    <ul>
                      <li>
                        <strong>Nombre de tu producto:</strong> Aquí debes poner el nombre del producto que aparece en tu carta.
                      </li>
                      <li>
                        <strong>Descripción (opcional):</strong> Añade detalles adicionales sobre tu producto para mayor claridad.
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              )}
            </div>

            {/* Nombre del Grupo */}
            <Form.Group controlId="groupName" className="mb-3">
              <Form.Label>
                Ingresa el nombre de tu producto <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ejemplo: Pizza, Hamburguesa, Ensalada..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                className='form-control-Selection-Group'
                autoFocus
              />
            </Form.Group>

            {/* Descripción del Grupo */}
            <Form.Group controlId="groupDescription" className="mb-3">
              <Form.Label>Describe tu producto (opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Añade una descripción que ayude a identificar mejor este producto..."
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                className='form-control-Selection-Group'
              />
            </Form.Group>

            {/* Separado de Impresión */}
            <div className="separate-printing-section">
              <div className="separate-printing-header">
                <Form.Label>Impresión Separada</Form.Label>
                <label className="custom-switch" htmlFor="separate-printing-switch">
                  <input 
                    type="checkbox" 
                    id="separate-printing-switch"
                    checked={separatePrinting}
                    onChange={(e) => setSeparatePrinting(e.target.checked)}
                  />
                  <span className="custom-slider"></span>
                </label>
              </div>
              
              <div className="separate-printing-info-banner">
                <div className="banner-content">
                  <FaInfoCircle className="banner-icon" />
                  <div className="banner-text">
                    <p>
                      <strong>Impresión Separada:</strong> Cuando está habilitada, genera comandas separadas para cada ítem del grupo. 
                      Por ejemplo, si un cliente pide "Hamburguesa con Papas", se crearán dos comandas: una para la hamburguesa y otra para las papas.
                    </p>
                    <p className="banner-sub-text">
                      Si está deshabilitada, solo se enviará "Hamburguesa con Papas" a la estación donde se prepara el producto principal.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
        )}
        
        <Modal.Footer>
          <Button 
            className="form-button-cancel" 
            variant="secondary" 
            onClick={handleModalClose} 
            disabled={loading}
            type="button"
          >
            Cancelar
          </Button>
          <Button 
            className="form-button-create" 
            variant="primary" 
            type="submit" 
            disabled={loading || !groupName.trim()}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span>Creando...</span>
              </>
            ) : (
              'Crear Grupo'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateSelectionGroupModal;
